/**
 * Las tres caras de las estadisticas del partido:
 *
 * - **Tabla (TAB)**: se mantiene pulsado y muestra la ficha de todos, estilo CS.
 * - **Aviso de gol**: un rotulo corto. Si el gol o la asistencia son TUYOS, lo
 *   dice — que es justo lo que da la palmadita en la espalda.
 * - **Resumen final**: marcador, MVP y la tabla completa.
 *
 * Todo es DOM sobre el canvas: no compite con el render de Pixi ni le roba
 * frames, y hereda la estetica del menu sin duplicar estilos.
 */
import { TEAM_IDENTITY, type TeamSide } from "@golazo/shared";
import { t } from "../i18n/index.js";
import { playerScore } from "../game/matchStats.js";
import type { GoalEvent, MatchStatsSnapshot, PlayerStat } from "../game/matchStats.js";
import "./scoreboard.css";

/*
 * La puntuacion de partido se mudo al modelo (`matchStats`) porque la carrera
 * la necesita para el record de "mejor partido" y no puede depender de una
 * pantalla. Se sigue re-exportando desde aqui: es donde la buscan los tests y
 * quien lea la tabla final.
 */
export { playerScore };

export interface ScoreboardHandle {
  /** Muestra u oculta la tabla (TAB mantenido). */
  setTableVisible(on: boolean): void;
  /** Refresca los datos de la tabla mientras esta abierta. */
  render(snapshot: MatchStatsSnapshot, score: Record<TeamSide, number>, humanId: string): void;
  /** Rotulo de gol. `humanId` decide si el mensaje es en primera persona. */
  announceGoal(event: GoalEvent, snapshot: MatchStatsSnapshot, humanId: string): void;
  /** Pantalla de fin de partido. */
  showSummary(snapshot: MatchStatsSnapshot, score: Record<TeamSide, number>, humanId: string): void;
  hideSummary(): void;
  dispose(): void;
}

/**
 * El nombre de un jugador, listo para meter en una plantilla de HTML.
 *
 * ESCAPA SIEMPRE, y esa es la razon de que esta funcion exista en vez de leer
 * `stat.nickname` a pelo: el apodo lo escribe OTRO jugador y llega por el
 * protocolo (`room.seats[].nickname` → `match:start` → `simulation` → aqui).
 * Todo lo que devuelve acaba en un `innerHTML` —tabla, rotulo de gol, MVP,
 * resumen—, asi que un apodo con `<img onerror=...>` se ejecutaba en el
 * navegador de todos los demas, con el token de su cuenta al alcance.
 *
 * `t()` solo sustituye `{claves}`, no vuelve a tocar el texto: escapar aqui
 * aguanta aunque el nombre pase por una traduccion.
 */
function nameOf(stat: PlayerStat | undefined): string {
  if (!stat) return "—";
  return stat.nickname ? escapeHtml(stat.nickname) : `#${stat.shirtNumber}`;
}

function findStat(snapshot: MatchStatsSnapshot, id: string | null): PlayerStat | undefined {
  if (!id) return undefined;
  return snapshot.players.find((p) => p.id === id);
}

/** Fila de la tabla. Los porteros van al final, el resto por puntuacion. */
function sortForTable(a: PlayerStat, b: PlayerStat): number {
  if ((a.role === "gk") !== (b.role === "gk")) return a.role === "gk" ? 1 : -1;
  if (b.goals !== a.goals) return b.goals - a.goals;
  if (b.assists !== a.assists) return b.assists - a.assists;
  return b.passesOk - a.passesOk;
}

function rowsHtml(players: PlayerStat[], side: TeamSide, humanId: string): string {
  return players
    /*
     * Sin porteros.
     *
     * La tabla mide goles, asistencias, tiros, pases y recuperaciones: cinco
     * columnas en las que un portero sale a cero por definicion, no por jugar
     * mal. Ocupaba una fila por equipo para no decir nada, y en 2v2 eso es la
     * MITAD de la tabla.
     */
    .filter((p) => p.side === side && p.role !== "gk")
    .sort(sortForTable)
    .map((p) => {
      const mine = p.id === humanId ? " is-you" : "";
      return `
        <tr class="sb-row${mine}">
          <td class="sb-num">${p.shirtNumber}</td>
          <td class="sb-name">${p.nickname ? escapeHtml(p.nickname) : t("Jugador {n}", { n: p.shirtNumber })}${
            p.id === humanId ? `<span class="sb-you">${t("TÚ")}</span>` : ""
          }</td>
          <td>${p.goals}</td>
          <td>${p.assists}</td>
          <td>${p.shots}</td>
          <td>${p.passesOk}</td>
          <td>${p.recoveries}</td>
        </tr>`;
    })
    .join("");
}

function tableHtml(
  snapshot: MatchStatsSnapshot,
  score: Record<TeamSide, number>,
  humanId: string,
): string {
  const head = `
    <tr>
      <th class="sb-num">#</th>
      <th class="sb-name">${t("Jugador")}</th>
      <th title="${t("Goles")}">${t("G")}</th>
      <th title="${t("Asistencias")}">${t("A")}</th>
      <th title="${t("Tiros")}">${t("T")}</th>
      <th title="${t("Pases completados")}">${t("P")}</th>
      <th title="${t("Recuperaciones")}">${t("R")}</th>
    </tr>`;
  return `
    <div class="sb-team sb-team--home">
      <header class="sb-team-head"><span class="sb-dot sb-dot--home"></span>${t("Local")}<em>${score.home}</em></header>
      <table class="sb-table"><thead>${head}</thead><tbody>${rowsHtml(snapshot.players, "home", humanId)}</tbody></table>
    </div>
    <div class="sb-team sb-team--away">
      <header class="sb-team-head"><span class="sb-dot sb-dot--away"></span>${t("Visitante")}<em>${score.away}</em></header>
      <table class="sb-table"><thead>${head}</thead><tbody>${rowsHtml(snapshot.players, "away", humanId)}</tbody></table>
    </div>`;
}

function statRowsHtml(
  players: PlayerStat[],
  side: TeamSide,
  humanId: string,
  mvpId: string | null,
): string {
  return players
    // Sin porteros, igual que la tabla del TAB: los lleva siempre la IA y en
    // estas columnas salen a cero por definicion, no por jugar mal.
    .filter((p) => p.side === side && p.role !== "gk")
    .sort((a, b) => playerScore(b) - playerScore(a))
    .map((p) => {
      const mine = p.id === humanId;
      const acc = p.shots > 0 ? Math.round((p.goals / p.shots) * 100) : 0;
      const passPct = p.passes > 0 ? Math.round((p.passesOk / p.passes) * 100) : 0;
      return `
        <tr class="fin-row${mine ? " is-you" : ""}${p.id === mvpId ? " is-mvp" : ""}">
          <td class="fin-rank">${p.id === mvpId ? "★" : ""}</td>
          <td class="fin-name">
            <span class="fin-num">${p.shirtNumber}</span>
            ${p.nickname ? escapeHtml(p.nickname) : t("Jugador {n}", { n: p.shirtNumber })}
            ${mine ? `<em>${t("TÚ")}</em>` : ""}
          </td>
          <td class="fin-score">${playerScore(p)}</td>
          <td>${p.goals}</td>
          <td>${p.assists}</td>
          <td>${p.shots}</td>
          <td class="fin-soft">${acc}%</td>
          <td>${p.recoveries}</td>
          <td class="fin-soft">${passPct}%</td>
          <td class="fin-soft">${p.losses}</td>
        </tr>`;
    })
    .join("");
}

function escapeHtml(text: string): string {
  return text
    .replaceAll("&", "&amp;")
    .replaceAll("<", "&lt;")
    .replaceAll(">", "&gt;")
    .replaceAll('"', "&quot;");
}

/**
 * Tabla final del partido, jugador a jugador.
 *
 * Estilo marcador de fin de ronda: una fila por jugador, ordenada por
 * puntuacion, con el equipo como cabecera y tu fila resaltada. Los datos ya
 * existian —los mismos que enseña el TAB durante el partido— pero al acabar no
 * se veian por ningun lado: el partido terminaba y todo lo que habias hecho
 * desaparecia.
 */
export function finalStatsHtml(
  snapshot: MatchStatsSnapshot,
  score: Record<TeamSide, number>,
  humanId: string,
): string {
  const mvpId = snapshot.mvp?.stat.id ?? null;
  const head = `
    <tr>
      <th class="fin-rank"></th>
      <th class="fin-name">${t("Jugador")}</th>
      <th class="fin-score" title="${t("Puntuación")}">${t("PTS")}</th>
      <th title="${t("Goles")}">${t("G")}</th>
      <th title="${t("Asistencias")}">${t("A")}</th>
      <th title="${t("Tiros")}">${t("T")}</th>
      <th class="fin-soft" title="${t("Tiros que acabaron en gol")}">${t("EFI")}</th>
      <th title="${t("Recuperaciones")}">${t("R")}</th>
      <th class="fin-soft" title="${t("Pases completados")}">${t("PAS")}</th>
      <th class="fin-soft" title="${t("Balones perdidos")}">${t("PER")}</th>
    </tr>`;

  const team = (side: TeamSide, label: string, goals: number): string => `
    <section class="fin-team fin-team--${side}">
      <header class="fin-team-head">
        <span class="fin-team-name">${label}</span>
        <span class="fin-team-score">${goals}</span>
      </header>
      <div class="fin-scroll">
        <table class="fin-table">
          <thead>${head}</thead>
          <tbody>${statRowsHtml(snapshot.players, side, humanId, mvpId)}</tbody>
        </table>
      </div>
    </section>`;

  return `
    <div class="fin-stats">
      ${team("home", escapeHtml(TEAM_IDENTITY.home.name), score.home)}
      ${team("away", escapeHtml(TEAM_IDENTITY.away.name), score.away)}
    </div>`;
}

/**
 * Bloque MVP + goleadores, listo para incrustar en la pantalla de resultado que
 * ya existe (`matchChrome`). Se devuelve como HTML para que esa capa no tenga
 * que conocer el modelo de estadisticas.
 */
export function mvpBlockHtml(
  snapshot: MatchStatsSnapshot,
  humanId: string,
  opts: { card?: boolean } = {},
): string {
  const mvp = snapshot.mvp;
  /*
   * La tarjeta se puede callar.
   *
   * Cuando el MVP eres tu, repite palabra por palabra la linea que ya esta
   * arriba en "TU PARTIDO" (2G · 0A · 13T · 7R) y encima con tu nombre al lado
   * de "eres tu". Dos bloques identicos seguidos no informan el doble: ocupan
   * el doble. El que llama decide, porque es el unico que sabe que hay arriba.
   */
  const head = mvp && opts.card !== false
    ? `<div class="result-mvp-card">
         <span class="result-mvp-badge">MVP</span>
         <span class="result-mvp-name">${
           mvp.stat.nickname ? escapeHtml(mvp.stat.nickname) : t("Jugador {n}", { n: mvp.stat.shirtNumber })
         }${mvp.stat.id === humanId ? ` · ${t("eres tú")}` : ""}</span>
         <span class="result-mvp-reason">${mvp.reason}</span>
         <span class="result-mvp-line">${mvp.stat.goals}G · ${mvp.stat.assists}A · ${mvp.stat.shots}T · ${mvp.stat.recoveries}R</span>
       </div>`
    : "";

  const goals = snapshot.goals.length
    ? `<ul class="result-goals">${snapshot.goals
        .map((g) => {
          const s = findStat(snapshot, g.scorerId);
          const a = findStat(snapshot, g.assistId);
          const min = Math.floor(g.atSecond / 60);
          const sec = String(g.atSecond % 60).padStart(2, "0");
          const who = g.ownGoal ? `${nameOf(s)} (p.p.)` : nameOf(s);
          const mine = g.scorerId === humanId || g.assistId === humanId ? " is-mine" : "";
          return `<li class="result-goal result-goal--${g.side}${mine}"><em>${min}:${sec}</em> ${who}${
            a ? ` <span>asist. ${nameOf(a)}</span>` : ""
          }</li>`;
        })
        .join("")}</ul>`
    : "";

  return head + goals;
}

export function createScoreboard(root: HTMLElement = document.body): ScoreboardHandle {
  const layer = document.createElement("div");
  layer.className = "sb-layer";
  layer.innerHTML = `
    <div class="sb-table-wrap is-hidden" aria-hidden="true">
      <div class="sb-panel"></div>
      <p class="sb-legend">${t("G goles · A asistencias · T tiros · P pases · R recuperaciones")}</p>
    </div>

    <div class="sb-toast-slot" aria-live="polite"></div>

    <div class="sb-summary is-hidden" role="dialog" aria-label="${t("Resumen del partido")}">
      <div class="sb-summary-panel">
        <header class="sb-summary-head">
          <p class="sb-summary-kicker">${t("Final del partido")}</p>
          <div class="sb-summary-score"></div>
        </header>
        <div class="sb-mvp"></div>
        <div class="sb-summary-body"></div>
        <footer class="sb-summary-foot">
          <button type="button" class="sb-summary-close">${t("Continuar")}</button>
        </footer>
      </div>
    </div>
  `;
  root.appendChild(layer);

  const tableWrap = layer.querySelector(".sb-table-wrap") as HTMLElement;
  const tablePanel = layer.querySelector(".sb-panel") as HTMLElement;
  const toastSlot = layer.querySelector(".sb-toast-slot") as HTMLElement;
  const summary = layer.querySelector(".sb-summary") as HTMLElement;
  const summaryScore = layer.querySelector(".sb-summary-score") as HTMLElement;
  const summaryBody = layer.querySelector(".sb-summary-body") as HTMLElement;
  const mvpBox = layer.querySelector(".sb-mvp") as HTMLElement;
  const closeBtn = layer.querySelector(".sb-summary-close") as HTMLButtonElement;

  closeBtn.addEventListener("click", () => summary.classList.add("is-hidden"));

  let tableOpen = false;

  return {
    setTableVisible(on: boolean): void {
      tableOpen = on;
      tableWrap.classList.toggle("is-hidden", !on);
      tableWrap.setAttribute("aria-hidden", on ? "false" : "true");
    },

    render(snapshot, score, humanId): void {
      if (!tableOpen) return;
      tablePanel.innerHTML = tableHtml(snapshot, score, humanId);
    },

    announceGoal(event, snapshot, humanId): void {
      const scorer = findStat(snapshot, event.scorerId);
      const assistant = findStat(snapshot, event.assistId);

      // El mensaje personal manda: si es tuyo, se dice en primera persona.
      let kicker = t("Gol");
      let line = "";
      let tone = "neutral";

      if (event.ownGoal) {
        kicker = t("En propia");
        line = scorer ? t("Desvío de {quien}", { quien: nameOf(scorer) }) : t("Se le mete solo");
        tone = "own";
      } else if (event.scorerId && event.scorerId === humanId) {
        kicker = t("¡Tu gol!");
        line = assistant
          ? t("Asistencia de {quien}", { quien: nameOf(assistant) })
          : t("Sin ayuda de nadie");
        tone = "mine";
      } else if (event.assistId && event.assistId === humanId) {
        kicker = t("¡Tu asistencia!");
        line = t("La empujó {quien}", { quien: nameOf(scorer) });
        tone = "mine";
      } else {
        kicker = t("Gol");
        line = assistant
          ? t("{goleador} · asistió {asistente}", {
              goleador: nameOf(scorer),
              asistente: nameOf(assistant),
            })
          : t("Lo marcó {quien}", { quien: nameOf(scorer) });
        tone = event.side === "home" ? "home" : "away";
      }

      const toast = document.createElement("div");
      toast.className = `sb-toast sb-toast--${tone}`;
      toast.innerHTML = `
        <strong class="sb-toast-kicker">${kicker}</strong>
        <span class="sb-toast-line">${line}</span>
      `;
      toastSlot.appendChild(toast);
      // Se limpia solo: el rotulo no debe sobrevivir al saque de centro.
      window.setTimeout(() => toast.classList.add("is-out"), 2600);
      window.setTimeout(() => toast.remove(), 3200);
    },

    showSummary(snapshot, score, humanId): void {
      const won = score.home > score.away;
      const draw = score.home === score.away;
      summaryScore.innerHTML = `
        <span class="sb-final-team">${escapeHtml(TEAM_IDENTITY.home.name)}</span>
        <span class="sb-final-num">${score.home}</span>
        <span class="sb-final-sep">–</span>
        <span class="sb-final-num">${score.away}</span>
        <span class="sb-final-team">${escapeHtml(TEAM_IDENTITY.away.name)}</span>
        <em class="sb-final-verdict">${draw ? t("Empate") : won ? t("Victoria local") : t("Victoria visitante")}</em>
      `;

      const mvp = snapshot.mvp;
      mvpBox.innerHTML = mvp
        ? `
          <div class="sb-mvp-badge">MVP</div>
          <div class="sb-mvp-body">
            <strong>${mvp.stat.nickname ? escapeHtml(mvp.stat.nickname) : t("Jugador {n}", { n: mvp.stat.shirtNumber })}${
              mvp.stat.id === humanId ? ` · ${t("eres tú")}` : ""
            }</strong>
            <span>${mvp.reason}</span>
            <span class="sb-mvp-line">${mvp.stat.goals}G · ${mvp.stat.assists}A · ${mvp.stat.shots}T · ${mvp.stat.recoveries}R</span>
          </div>
          <div class="sb-mvp-num">#${mvp.stat.shirtNumber}</div>`
        : `<div class="sb-mvp-body"><strong>${t("Sin MVP")}</strong><span>${t("Partido sin nada que destacar.")}</span></div>`;

      const goalList = snapshot.goals.length
        ? `<ol class="sb-goals">${snapshot.goals
            .map((g) => {
              const s = findStat(snapshot, g.scorerId);
              const a = findStat(snapshot, g.assistId);
              const min = Math.floor(g.atSecond / 60);
              const sec = String(g.atSecond % 60).padStart(2, "0");
              const who = g.ownGoal ? `${nameOf(s)} (p.p.)` : nameOf(s);
              return `<li class="sb-goal sb-goal--${g.side}"><span class="sb-goal-time">${min}:${sec}</span><span>${who}${
                a ? ` <em>asist. ${nameOf(a)}</em>` : ""
              }</span></li>`;
            })
            .join("")}</ol>`
        : `<p class="sb-empty">${t("Sin goles.")}</p>`;

      summaryBody.innerHTML = `${goalList}<div class="sb-panel sb-panel--summary">${tableHtml(
        snapshot,
        score,
        humanId,
      )}</div>`;
      summary.classList.remove("is-hidden");
      closeBtn.focus();
    },

    hideSummary(): void {
      summary.classList.add("is-hidden");
    },

    dispose(): void {
      layer.remove();
    },
  };
}
